import { useEffect, useLayoutEffect } from 'react';

/** LZ-string share payload in `?s=` - not a distinct indexable page. */
export const SHARE_QUERY_PARAM = 's';

/** Robots directive for share URLs (canonical points at the clean path). */
export const SHARE_URL_ROBOTS = 'noindex, follow';

interface PageMetaOptions {
  title: string;
  description: string;
  /**
   * Path starting with "/", e.g. "/", "/about"
   */
  path: string;
  imagePath?: string;
  imageAlt?: string;
  /** Sets <meta name="robots"> - e.g. "noindex, nofollow" for 404 pages */
  robots?: string;
}

export const SITE_ORIGIN = 'https://mortgagecomparecalculator.com';
const DEFAULT_SOCIAL_IMAGE = '/social-card.png';
const DEFAULT_SOCIAL_IMAGE_ALT = 'Mortgage Compare Calculator dashboard preview';

export function hasShareQueryParam(
  search: string | URLSearchParams = typeof window !== 'undefined' ? window.location.search : '',
): boolean {
  const params =
    typeof search === 'string'
      ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
      : search;
  return params.has(SHARE_QUERY_PARAM);
}

/** Split a route or request URL into pathname and optional `?query` (no hash). */
export function splitRouteUrl(url: string): { pathname: string; search: string } {
  const withoutHash = url.split('#')[0] ?? url;
  const qIndex = withoutHash.indexOf('?');
  if (qIndex === -1) {
    return { pathname: withoutHash || '/', search: '' };
  }
  return {
    pathname: withoutHash.slice(0, qIndex) || '/',
    search: withoutHash.slice(qIndex),
  };
}

export function buildCanonicalUrl(pathname: string): string {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return `${SITE_ORIGIN}${path}`;
}

function upsertMeta(
  selector: string,
  attributes: Record<string, string>
) {
  if (typeof document === 'undefined') return;
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    el!.setAttribute(key, value);
  });
}

function upsertCanonical(url: string) {
  if (typeof document === 'undefined') return;
  let link = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

export interface ShareUrlHeadOptions {
  canonicalPath: string;
  robots?: string;
}

/** Set canonical, og:url, and optional robots for a share URL (`?s=` present). */
export function applyShareUrlHeadTags({
  canonicalPath,
  robots = SHARE_URL_ROBOTS,
}: ShareUrlHeadOptions): void {
  if (typeof document === 'undefined') return;

  const canonicalUrl = buildCanonicalUrl(canonicalPath);

  upsertCanonical(canonicalUrl);
  upsertMeta("meta[name='robots']", { name: 'robots', content: robots });
  upsertMeta("meta[property='og:url']", {
    property: 'og:url',
    content: canonicalUrl,
  });
}

/**
 * Runs before React hydrates so crawlers see canonical/noindex even if `?s=` is
 * stripped immediately after load.
 */
export function applyEarlyShareUrlSeo(): void {
  if (typeof window === 'undefined' || !hasShareQueryParam()) return;
  applyShareUrlHeadTags({ canonicalPath: window.location.pathname || '/' });
}

function applyPageHeadTags({
  title,
  description,
  path,
  imagePath,
  imageAlt,
  robots,
}: PageMetaOptions): void {
  if (typeof document === 'undefined') return;

  const isShareUrl = hasShareQueryParam();
  const canonicalUrl = buildCanonicalUrl(path);
  const imageUrl = imagePath.startsWith('http') ? imagePath : `${SITE_ORIGIN}${imagePath}`;
  const robotsContent = robots ?? (isShareUrl ? SHARE_URL_ROBOTS : undefined);

  document.title = title;

  upsertMeta("meta[name='description']", {
    name: 'description',
    content: description,
  });

  if (robotsContent) {
    upsertMeta("meta[name='robots']", { name: 'robots', content: robotsContent });
  } else {
    document.querySelector<HTMLMetaElement>("meta[name='robots']")?.remove();
  }

  upsertCanonical(canonicalUrl);

  upsertMeta("meta[property='og:title']", {
    property: 'og:title',
    content: title,
  });

  upsertMeta("meta[property='og:description']", {
    property: 'og:description',
    content: description,
  });

  upsertMeta("meta[property='og:url']", {
    property: 'og:url',
    content: canonicalUrl,
  });

  upsertMeta("meta[property='og:type']", {
    property: 'og:type',
    content: 'website',
  });

  upsertMeta("meta[property='og:image']", {
    property: 'og:image',
    content: imageUrl,
  });

  upsertMeta("meta[property='og:image:alt']", {
    property: 'og:image:alt',
    content: imageAlt,
  });

  upsertMeta("meta[name='twitter:card']", {
    name: 'twitter:card',
    content: 'summary_large_image',
  });

  upsertMeta("meta[name='twitter:title']", {
    name: 'twitter:title',
    content: title,
  });

  upsertMeta("meta[name='twitter:description']", {
    name: 'twitter:description',
    content: description,
  });

  upsertMeta("meta[name='twitter:image']", {
    name: 'twitter:image',
    content: imageUrl,
  });

  upsertMeta("meta[name='twitter:image:alt']", {
    name: 'twitter:image:alt',
    content: imageAlt,
  });
}

/**
 * Hook to keep document title, meta description, canonical and basic OG tags
 * in sync with the current React route.
 */
export function usePageMeta({
  title,
  description,
  path,
  imagePath = DEFAULT_SOCIAL_IMAGE,
  imageAlt = DEFAULT_SOCIAL_IMAGE_ALT,
  robots,
}: PageMetaOptions) {
  const metaOptions = { title, description, path, imagePath, imageAlt, robots };

  useLayoutEffect(() => {
    if (hasShareQueryParam()) {
      applyShareUrlHeadTags({ canonicalPath: path });
    }
  }, [path]);

  useEffect(() => {
    applyPageHeadTags(metaOptions);
  }, [title, description, path, imagePath, imageAlt, robots]);
}

